import React, { useState } from 'react';
import { Button, Modal, Input } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
// Api
import { REVIEW_CHANNEL_MONETIZATION_APPLICATION } from 'api/channelMonetization/mutations';
// Types
import { ListChannelMonetizationApplications_listChannelMonetizationApplications_entities } from 'api/channelMonetization/types/ListChannelMonetizationApplications';
import {
  ReviewChannelMonetizationApplication,
  ReviewChannelMonetizationApplicationVariables,
} from 'api/channelMonetization/types/ReviewChannelMonetizationApplication';
import { ChannelMonetizationApplicationStatus } from 'api/graphql-global-types';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Styles
import styles from './ApplicationActions.module.scss';

type ApplicationActionsProps = {
  application: ListChannelMonetizationApplications_listChannelMonetizationApplications_entities;
  onReviewed: () => void;
};

const ApplicationActions: React.FC<ApplicationActionsProps> = ({
  application,
  onReviewed,
}) => {
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const [reviewApplication, { loading }] = useMutation<
    ReviewChannelMonetizationApplication,
    ReviewChannelMonetizationApplicationVariables
  >(REVIEW_CHANNEL_MONETIZATION_APPLICATION);

  const review = async (
    decision: ChannelMonetizationApplicationStatus,
    reason?: string
  ) => {
    try {
      await reviewApplication({
        variables: {
          input: {
            applicationId: application.id,
            decision,
            rejectionReason: reason,
          },
        },
      });
      successNotification(`Application ${decision.toLowerCase()}`);
      onReviewed();
    } catch (error) {
      errorNotification((error as Error).message);
    }
  };

  const handleApprove = () => {
    review(ChannelMonetizationApplicationStatus.Approved);
  };

  const handleConfirmReject = () => {
    review(
      ChannelMonetizationApplicationStatus.Rejected,
      rejectionReason.trim() || undefined
    );
    setRejectModalOpen(false);
    setRejectionReason('');
  };

  if (application.status !== ChannelMonetizationApplicationStatus.Review) {
    return <span className={styles.reviewed}>Reviewed</span>;
  }

  return (
    <div className={styles.actionsContainer}>
      <Button
        type="link"
        icon={<CheckOutlined />}
        onClick={handleApprove}
        loading={loading}
      >
        Approve
      </Button>
      <Button
        type="link"
        danger
        icon={<CloseOutlined />}
        onClick={() => setRejectModalOpen(true)}
        disabled={loading}
      >
        Reject
      </Button>

      <Modal
        title="Reject Application"
        visible={rejectModalOpen}
        onOk={handleConfirmReject}
        onCancel={() => setRejectModalOpen(false)}
        okText="Reject"
        okButtonProps={{ danger: true }}
      >
        <Input.TextArea
          rows={3}
          placeholder="Reason for rejection (optional, shown to the creator)"
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default ApplicationActions;
